/**
 * An SOP's scenario types and how many scenarios of each kind sit under each, from one call.
 * Read as a query rather than persisted: the counts move whenever past tickets are bucketed or
 * scenarios fabricated, so a copy that outlived the page would report scenarios the SOP no
 * longer has.
 */

import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { queryClient } from '@/lib/query-client';
import { aiFunctionService, type ScenarioCounts, type TypeReconcileSummary } from '@/services/ai-function.service';
import type { TypeReconcileProgress } from '@/types/websocket.types';
import { toLocalType, type ScenarioType } from '@/modules/back-testing/types';

export interface SopTypes {
	types: ScenarioType[];
	/** Scenario counts by type id, plus `none` for the past tickets that fit no type. */
	counts: Record<string, ScenarioCounts>;
	lastReconcile: TypeReconcileSummary | null;
	reconciling: TypeReconcileProgress | null;
	reconcileFailedAt: string | null;
	protocolCompiling: boolean;
}

/** A set with nothing compiling or reconciling over it. */
const SETTLED = { lastReconcile: null, reconciling: null, reconcileFailedAt: null, protocolCompiling: false };

/** How often the list is read while a compile or its reconcile runs, in case a pushed update is missed. */
const RECONCILE_POLL_MS = 5000;

export const backTestingTypesKey = (sopId: string): string[] => ['back-testing', sopId, 'types'];

const backTestingTypesQueryAtom = (sopId: string) =>
	atomWithQuery(() => ({
		queryKey: backTestingTypesKey(sopId),
		queryFn: async (): Promise<SopTypes> => {
			const data = await aiFunctionService.fetchTypes(sopId);
			return {
				types: data.types.map((t) => toLocalType(sopId, t)),
				counts: data.counts,
				lastReconcile: data.lastReconcile ?? null,
				reconciling: data.reconciling ?? null,
				reconcileFailedAt: data.reconcileFailedAt ?? null,
				protocolCompiling: data.protocolCompiling ?? false
			};
		},
		enabled: Boolean(sopId),
		// Generating types, deleting and bucketing all write the answer back themselves,
		// so this only covers a screen left open while someone else changes the set.
		staleTime: 2 * 60 * 1000,
		refetchInterval: (query) =>
			query.state.data?.reconciling || query.state.data?.protocolCompiling ? RECONCILE_POLL_MS : false
	}));

export function useBackTestingTypes(sopId: string) {
	const queryAtom = useMemo(() => backTestingTypesQueryAtom(sopId), [sopId]);
	const [query] = useAtom(queryAtom);
	return query;
}

/** Fetches the types and counts again; the caller has just changed them on the server. */
export const reloadBackTestingTypes = (sopId: string): Promise<void> =>
	queryClient.invalidateQueries({ queryKey: backTestingTypesKey(sopId) }).then(() => undefined);

/**
 * A generated set replaces the whole list, and the counts go with it: the new set has
 * fresh type ids and the server drops every scenario under the old ones, so the old
 * counts describe types that no longer exist. Zero is the truth until bucketing reports.
 */
export const setBackTestingTypes = (sopId: string, types: ScenarioType[]): void => {
	queryClient.setQueryData<SopTypes>(backTestingTypesKey(sopId), { types, counts: {}, ...SETTLED });
};

/** A compile and the check of the types after it rewrite the set, so nothing else writes to it meanwhile. */
export const typeSetBusy = (sopTypes: SopTypes | undefined): boolean =>
	Boolean(sopTypes?.reconciling) || (Boolean(sopTypes?.protocolCompiling) && Boolean(sopTypes?.types.length));

/** The SOP's protocol started compiling, as the server pushed it: the list is read until the check after it lands. */
export const setProtocolCompiling = (sopId: string): void => {
	queryClient.setQueryData<SopTypes>(backTestingTypesKey(sopId), (prev) =>
		prev ? { ...prev, protocolCompiling: true } : prev
	);
};

/** A running reconcile's latest progress, as the server pushed it; the compile behind it has landed. */
export const setReconcileProgress = (sopId: string, progress: TypeReconcileProgress): void => {
	queryClient.setQueryData<SopTypes>(backTestingTypesKey(sopId), (prev) =>
		prev ? { ...prev, reconciling: progress, protocolCompiling: false } : prev
	);
};

/** Drops one type and its count. */
export const removeBackTestingType = (sopId: string, typeId: string): void => {
	queryClient.setQueryData<SopTypes>(backTestingTypesKey(sopId), (prev) => {
		if (!prev) return prev;
		const counts = { ...prev.counts };
		delete counts[typeId];
		return { ...prev, types: prev.types.filter((t) => t.id !== typeId), counts };
	});
};

/** Everything back testing held for one SOP is gone; the next read starts from the server. */
export const clearBackTestingTypes = (sopId: string): void => {
	queryClient.removeQueries({ queryKey: backTestingTypesKey(sopId) });
};
